import { apiFetch } from './client'

export interface BillingStatus {
  company_id: number
  company_name: string
  subscription_status: string
  current_period_end: string | null
  grace_ends_at: string | null
  grace_days_left: number | null
  comped_until: string | null
  billing_note: string | null
  owner_email: string
  blocked: boolean
  read_only: boolean
  show_grace_banner: boolean
  reason: string | null
}

export async function fetchBillingStatus(): Promise<BillingStatus> {
  const json = await apiFetch<{ data: BillingStatus }>('GET', '/api/billing/status')
  return json.data
}

export interface AdminCustomerRow {
  id: number
  company_name: string
  owner_email: string | null
  subscription_status: string | null
  current_period_end: string | null
  grace_ends_at: string | null
  comped_until: string | null
  billing_note: string | null
  project_count?: number
  can_delete?: boolean
  delete_block_reason?: string | null
}

export interface AdminCustomersResponse {
  rows: AdminCustomerRow[]
  tiles: {
    paying: number; comped: number; grace: number; locked: number; total: number
    mrr?: number; arr?: number; failed?: number; renewals30?: number
    price_configured?: boolean
  }
}

export interface AccessGrantRow {
  id: number
  email: string
  grant_type: string
  consumed_at: string | null
  consumed_company_id: number | null
}

export async function fetchAccessGrant(email: string): Promise<AccessGrantRow | null> {
  const json = await apiFetch<{ data: AccessGrantRow | null }>(
    'GET',
    `/api/admin/access-grants?email=${encodeURIComponent(email)}`,
  )
  return json.data
}

export async function fetchAdminCustomers(): Promise<AdminCustomersResponse> {
  const json = await apiFetch<{ data: AdminCustomersResponse }>('GET', '/api/admin/customers')
  return json.data
}

export async function createAccessGrant(payload: {
  email: string
  grant_type: 'comped' | 'checkout_required' | 'exempt'
  comped_until?: string
  note?: string
}): Promise<void> {
  await apiFetch('POST', '/api/admin/access-grants', payload)
}

export async function adminMarkPaid(companyId: number, note?: string): Promise<void> {
  await apiFetch('POST', `/api/admin/customers/${companyId}/mark-paid`, { note })
}

export async function adminComp(companyId: number, compedUntil: string, note?: string): Promise<void> {
  await apiFetch('POST', `/api/admin/customers/${companyId}/comp`, { comped_until: compedUntil, note })
}

export async function adminLock(companyId: number, note?: string): Promise<void> {
  await apiFetch('POST', `/api/admin/customers/${companyId}/lock`, { note })
}

export async function adminCheckoutLink(companyId: number): Promise<string> {
  const json = await apiFetch<{ data: { url: string } }>('POST', `/api/admin/customers/${companyId}/checkout-link`, {})
  return json.data.url
}

export async function adminExempt(companyId: number, note: string): Promise<void> {
  await apiFetch('POST', `/api/admin/customers/${companyId}/exempt`, { note })
}

export async function adminDeleteCompany(companyId: number, confirmName: string): Promise<void> {
  await apiFetch('DELETE', `/api/admin/customers/${companyId}`, { confirm_name: confirmName })
}

export interface BillingPlan {
  configured: boolean
  amount_cents: number
  currency: string
  interval: string
  billing_model: string
}

export async function fetchBillingPlan(): Promise<BillingPlan> {
  const json = await apiFetch<{ data: BillingPlan }>('GET', '/api/billing/plan')
  return json.data
}

export async function startBillingCheckout(promotionCode?: string): Promise<string> {
  const json = await apiFetch<{ data: { url: string } }>('POST', '/api/billing/checkout', {
    promotion_code: promotionCode || undefined,
  })
  return json.data.url
}

export interface BillingReturnContext {
  email: string | null
  paid: boolean
}

export async function fetchBillingReturnContext(sessionId: string): Promise<BillingReturnContext> {
  const json = await apiFetch<{ data: BillingReturnContext }>(
    'GET',
    `/api/billing/return-context?session_id=${encodeURIComponent(sessionId)}`,
  )
  return json.data
}

export async function completeBillingReturn(sessionId: string): Promise<void> {
  await apiFetch('POST', '/api/billing/complete-return', { session_id: sessionId })
}

export async function openBillingPortal(): Promise<string> {
  const json = await apiFetch<{ data: { url: string } }>('POST', '/api/billing/portal', {})
  return json.data.url
}
